"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { isBlogPath } from "@/lib/blog/is-blog-path";

/**
 * Sets `data-scrolling` on <html> while a scroll is in flight and removes it
 * ~150ms after it stops. A MOBILE-ONLY CSS rule (globals.css) keys off this to
 * pause decorative keyframe animations during active scroll — the many small
 * ambient pulses/twinkles/drifts repaint every frame, and on a phone that's the
 * single biggest drag on the typical scroll frame.
 *
 * Why pause instead of "make them cheaper": measured null results — opacity-only
 * (dropping the invisible scale) and layer-promotion both failed to help; only
 * the animations NOT running during scroll recovers the frame budget. Pausing is
 * imperceptible (you don't watch a 6px dot pulse while flinging the page) and
 * `animation-play-state` resumes each animation exactly where it left off, so
 * there is zero visual jump on release. Desktop is untouched — the CSS rule is
 * inside a max-width:767px query, so this attribute does nothing there.
 *
 * Measured (4x CPU throttle, Pixel-5 emulation): typical scroll frame 50ms→33ms
 * (≈20fps→30fps), avg ~22.5→~25.8 fps.
 */
export function ScrollActivity() {
  const pathname = usePathname();
  // Founder ban (blog spec, decision #2): no scroll listeners driving visuals
  // on blog surfaces — and the attribute has no consumers there anyway.
  const onBlog = isBlogPath(pathname);

  useEffect(() => {
    if (onBlog) return;
    const el = document.documentElement;
    let scrolling = false;
    let timer: number | undefined;

    const onScroll = () => {
      // Flip the attribute ONCE per scroll burst, not per event: the
      // [data-scrolling] selector lives on <html>, so toggling it every event
      // would force a global style re-match each frame (self-defeating).
      if (!scrolling) {
        scrolling = true;
        el.setAttribute("data-scrolling", "");
      }
      clearTimeout(timer);
      timer = window.setTimeout(() => {
        scrolling = false;
        el.removeAttribute("data-scrolling");
      }, 150);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
      el.removeAttribute("data-scrolling");
    };
  }, [onBlog]);

  return null;
}
